import { langs, type Lang } from "@/langs";
import { Children, Fragment, isValidElement } from "react";
import Link from "../_components/Link";

export type TranslationDictionary<Keys extends string> = {
  [K in Keys]: Record<Exclude<Lang, "en">, string>;
};

export function renderLocaleContent<Keys extends string>(
  dictionary: TranslationDictionary<Keys>,
  key: Keys,
  langString: string,
  isStringOnly = false,
  dynamicTexts?: Record<string, string | React.ReactNode>,
  preserveMarks = false,
) {
  const lang = langs.find((l) => l === langString) ?? "en";
  let content = lang === "en" ? (key as string) : dictionary[key][lang];

  // Replace dynamicTexts if they're strings
  // this would allow rendering support with dynamicTexts, like links
  if (dynamicTexts) {
    for (const [key, value] of Object.entries(dynamicTexts)) {
      if (typeof value === "string") {
        const regex = new RegExp(`\\{${key}\\}`, "g");
        content = content.replace(regex, value);
      }
    }
  }

  const parts = content
    ? content.split(
        /(\*\*.*?\*\*|\*.*?\*|\[.*?\]\(.*?\)|<underline>.*?<\/underline>|<br \/>|\{.*?\})/,
      )
    : [];

  const renderedContent = (
    <>
      {parts.map((part, index) => {
        if (part.startsWith("{") && part.endsWith("}")) {
          const placeholder = part.slice(1, -1);
          const dynamicContent = dynamicTexts?.[placeholder];
          if (typeof dynamicContent === "string") {
            if (preserveMarks) return dynamicContent;
            return <Fragment key={index}>{dynamicContent}</Fragment>;
          } else if (isValidElement(dynamicContent)) {
            if (preserveMarks) return dynamicContent;
            return <Fragment key={index}>{dynamicContent}</Fragment>;
          }
          return part;
        } else if (part.startsWith("**") && part.endsWith("**")) {
          if (preserveMarks) return part;
          const text = part.slice(2, -2);
          return <strong key={index}>{text}</strong>;
        } else if (part.startsWith("*") && part.endsWith("*")) {
          if (preserveMarks) return part;
          const text = part.slice(1, -1);
          return <em key={index}>{text}</em>;
        } else if (part === "<br />") {
          if (preserveMarks) return part;
          return <br key={index} />;
        } else if (
          part.startsWith("<underline>") &&
          part.endsWith("</underline>")
        ) {
          if (preserveMarks) return part;
          const text = part.slice(11, -12);
          return (
            <span className="underline" key={index}>
              {text}
            </span>
          );
        } else if (part.startsWith("[") && part.includes("](")) {
          if (preserveMarks) return part;
          const [text, url] = part.slice(1, -1).split("](");

          if (
            url &&
            (url.startsWith("/") || new URL(url).hostname === "borderless.so")
          ) {
            return (
              <Link key={index} href={url} className="underline">
                {text}
              </Link>
            );
          }

          return (
            <a
              key={index}
              href={url}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {text}
            </a>
          );
        } else {
          return part;
        }
      })}
    </>
  );

  if (isStringOnly) {
    // stringify
    return extractTextContent(renderedContent);
  } else {
    return renderedContent;
  }
}

function extractTextContent(element: string | React.ReactNode): string {
  if (typeof element === "string") {
    return element;
  }

  if (isValidElement(element)) {
    const children = Children.toArray(
      (element.props as React.PropsWithChildren).children,
    );
    return children
      .map((child) => extractTextContent(child as string | React.ReactNode))
      .join("");
  }

  return "";
}
